
import { itemArtNode } from "./itemart";
import { skillGradeFor, skillIconNode } from "./itemicon";
import { elem, installChrome, styleInstaller } from "./chrome";

/** Slots on one page, and so the number of cells the taskbar draws.
 *  `QUICKSLOT_MAX_COUNT_PER_LINE = 8` (`client/src/UserInterface/Packet.h:382`). */
export const QUICK_SLOTS = 8;

/** Pages of them. `QUICKSLOT_MAX_LINE = 4`
 *  (`client/src/UserInterface/Packet.h:381`). */
export const QUICK_SLOT_PAGES = 4;

/** The whole table the character carries. `QUICKSLOT_MAX_NUM = 36`
 *  (`common/length.h:31`) -- four more than the `QUICKSLOT_MAX_COUNT = 32` its
 *  own client pages (`client/src/UserInterface/Packet.h:383`): slots 32-35 are
 *  reachable by the server and by nothing on the original's UI. */
export const QUICK_SLOT_TOTAL = 36;

/** `QUICKSLOT_TYPE_NONE`, in the original's own order (`common/length.h:229`). */
export const SLOT_TYPE_NONE = 0;
/** `QUICKSLOT_TYPE_ITEM` (`common/length.h:230`). `target` is a bag cell. */
export const SLOT_TYPE_ITEM = 1;
/** `QUICKSLOT_TYPE_SKILL` (`common/length.h:231`). `target` is a skill vnum. */
export const SLOT_TYPE_SKILL = 2;
/** `QUICKSLOT_TYPE_COMMAND` (`common/length.h:232`). `target` is an emotion. */
export const SLOT_TYPE_COMMAND = 3;

/** The original's own quickslot cells are 32x32: `x_step`/`y_step` on both
 *  `quick_slot_1` and `quick_slot_2` (`locale/en/ui/taskbar.py:402-403,430-431`).
 *  Two 4x1 grids, start_index 0 and 4, with a 14px ExpandButton between them. */
export const QUICK_SLOT_SIZE_PX = 32;

/** `kind` 3 is `ItemDef.usable` (`server/ground.py:134`). What a cell with no
 *  painting of its own draws as, so a bound slot still shows something. */
export const USABLE_KIND = 3;

/** Drop target for a bag or skill drag. Taskbar cells and this bar's own
 *  buttons both carry it so `elementFromPoint` at pointer-up can bind without
 *  either panel importing the other. It names a **local** index, 0-7. */
export const QUICK_SLOT_INDEX_ATTR = "data-quick-index";

/** One bag slot, as much of it as this needs. */
export interface QuickBagSlot {
  readonly slot: number;
  readonly vnum: number;
  readonly count: number;
  readonly name: string;
  readonly kind: number;
}

export interface QuickBag {
  readonly slots: readonly QuickBagSlot[];
}

/** One skill, as much of it as a Fast Access Field needs to paint. */
export interface QuickSkill {
  readonly vnum: number;
  readonly name: string;
  readonly icon: string;
  readonly level: number;
}

/** One occupied slot off a `QuickSlots` frame. Structurally the wire's
 *  `QuickSlotEntry`, restated here so this panel does not import the net stack. */
export interface QuickSlotState {
  readonly pos: number;
  readonly type: number;
  readonly target: number;
}

/** What a slot holds, as the three `target` readings of `SetQuickslot`
 *  (`char_quickslot.cpp:65-84`). Discriminated because a bag cell, a skill vnum
 *  and an emotion id are three different numbers. */
export type QuickBinding =
  | { readonly kind: "item"; readonly cell: number }
  | { readonly kind: "skill"; readonly vnum: number }
  | { readonly kind: "command"; readonly emotion: number };

export interface QuickSlotActions {
  /** Use the item in a bag cell. The *same* call `BagActions.onUse` makes, so a
   *  hotkey and a click on the bag are one code path from here down. */
  onUse?: (cell: number) => void;
  /** Cast the bound skill. The same call the skill bar's own click makes. */
  onUseSkill?: (vnum: number) => void;
  /** Play the bound gesture -- `BINARY_ActEmotion` (`PythonPlayer.cpp:832-836`). */
  onEmote?: (emotion: number) => void;
  /** Ask the server to put something on a slot (`QuickSlotAdd`). */
  onAdd?: (pos: number, type: number, target: number) => void;
  /** Ask the server to empty a slot (`QuickSlotDel`). */
  onDel?: (pos: number) => void;
  /** True while something else owns the keyboard -- the chat box, mainly. The
   *  bar asks at keystroke time rather than being told, so it never holds a
   *  stale answer. */
  blocked?: () => boolean;
  /** The drawn bar changed: a new table from the server, or a new page. The
   *  taskbar paints from this bar and is hidden from it, so it has to be told. */
  onChange?: () => void;
}

export interface QuickSlotOptions {
  /** Install the key listener. Off in tests that drive `fire` directly. */
  readonly keys?: boolean;
}

const STYLE_ID = "quickslot-style";

const CSS = `
/* The taskbar already paints the eight Slot_Base cells. This bar is the
   key logic; its own strip is hidden so a player does not see
   two hotbars. Tests still mount it. */
.quickslots {
  position: fixed;
  left: calc(50% - 86px);
  bottom: 0;
  z-index: 4;
  display: flex;
  flex-wrap: wrap;
  width: 283px;
  height: 37px;
  gap: 0;
  padding: 0;
  pointer-events: auto;
  font: 11px/1 Tahoma, "MS Sans Serif", sans-serif;
  color: #f3e7c1;
}
.quickslots[hidden] { display: none; }
.quickslot {
  position: relative;
  width: ${QUICK_SLOT_SIZE_PX}px;
  height: ${QUICK_SLOT_SIZE_PX}px;
  margin-top: 3px;
  padding: 0;
  border-radius: 0;
  border: 0;
  background: url("/ui/textures/public/slot_base.png") center / 32px 32px no-repeat;
  color: inherit;
  font: inherit;
  cursor: pointer;
  overflow: hidden;
  display: flex;
  align-items: center;
  justify-content: center;
  image-rendering: pixelated;
}
.quickslot:nth-child(5) { margin-left: 46px; }
.quickslot:hover { background: rgba(255, 236, 170, 0.16); }
/* Bound, but the cell under it holds nothing right now. Dimmed rather than
   cleared: the binding is the server's, and clearing one is the server's too
   (SyncQuickslot, char_quickslot.cpp:12-34). */
.quickslot-empty { opacity: 0.35; }
.quickslot-free { border-style: dashed; }
.quickslot-key {
  position: absolute;
  top: 2px;
  left: 4px;
  font-size: 9px;
  color: #9aa7b4;
  font-variant-numeric: tabular-nums;
}
.quickslot-count {
  position: absolute;
  bottom: 1px;
  right: 3px;
  font-size: 9px;
  font-weight: 700;
  color: #ffe9a8;
  text-shadow: 0 0 3px #000, 0 0 3px #000;
  font-variant-numeric: tabular-nums;
}
.quickslot-plus {
  font-size: 16px;
  color: #6f7a86;
  line-height: 1;
}
.quickslot .item-icon,
.quickslot .skill-icon { height: 26px; margin: 0; }
.quickslot svg { width: 22px; height: 22px; }

@media (max-width: 620px) {
  /* The skill bar shrinks to 74 + 14, so its top edge is at 100. */
  .quickslots { bottom: 108px; }
  .quickslot { width: ${QUICK_SLOT_SIZE_PX}px; height: ${QUICK_SLOT_SIZE_PX}px; }
}
`;

const installStyle = styleInstaller(STYLE_ID, CSS, installChrome);

export function keyFor(localIndex: number): string {
  if (localIndex < 4) return String(localIndex + 1);
  return `F${String(localIndex - 3)}`;
}

/** Inverse of `keyFor`. Null for a key this bar does not own -- which includes
 *  the digits 5-9: `__PressNumKey`'s unmodified arm is `if num >= 1 and num <=
 *  4` and nothing else (`game.py:431-432`), the rest being reserved for quick
 *  slots the original never gave a key (`game.py:319`). */
export function indexForKey(key: string): number | null {
  const digit = /^([1-9])$/.exec(key);
  if (digit !== null) {
    const n = Number(digit[1]);
    return n <= 4 ? n - 1 : null;
  }
  const fkey = /^F([1-9]|1[0-2])$/.exec(key);
  if (fkey === null) return null;
  const f = Number(fkey[1]);
  return f <= 4 ? f + 3 : null;
}

export function indexForCode(code: string): number | null {
  const digit = /^Digit([1-9])$/.exec(code);
  if (digit !== null) {
    const n = Number(digit[1]);
    return n <= 4 ? n - 1 : null;
  }
  return indexForKey(code);
}

/** The page a page number lands on, wrapping both ways exactly as
 *  `CPythonPlayer::SetQuickPage` does (`PythonPlayer.cpp:692-699`): below zero
 *  is `QUICKSLOT_MAX_LINE + n`, at or above four is `n % 4`. */
export function pageOf(n: number): number {
  if (n < 0) return QUICK_SLOT_PAGES + n;
  if (n >= QUICK_SLOT_PAGES) return n % QUICK_SLOT_PAGES;
  return n;
}

/** Global index of a local one on a page.
 *  `LocalQuickSlotIndexToGlobalQuickSlotIndex` (`PythonPlayer.cpp:704-707`). */
export function globalIndexOf(page: number, localIndex: number): number {
  return page * QUICK_SLOTS + localIndex;
}

/** A learned skill. Level 0 is a face on the character window, not a hotkey. */
export function hotkeyableSkill(skill: QuickSkill): boolean {
  return skill.vnum > 0 && skill.level > 0;
}

/** One `QuickSlots` frame, read into the thirty-six. Only occupied slots are on
 *  the frame, so a `pos` with no entry is an empty slot and the table is built
 *  from the frame rather than patched: the server sends all of it, every time. */
export function bindingsFrom(entries: readonly QuickSlotState[]): (QuickBinding | null)[] {
  const out: (QuickBinding | null)[] = Array<QuickBinding | null>(QUICK_SLOT_TOTAL).fill(null);
  for (const entry of entries) {
    if (!Number.isInteger(entry.pos) || entry.pos < 0 || entry.pos >= QUICK_SLOT_TOTAL) continue;
    out[entry.pos] = bindingOf(entry.type, entry.target);
  }
  return out;
}

function bindingOf(type: number, target: number): QuickBinding | null {
  switch (type) {
    case SLOT_TYPE_ITEM:
      return { kind: "item", cell: target };
    case SLOT_TYPE_SKILL:
      return { kind: "skill", vnum: target };
    case SLOT_TYPE_COMMAND:
      return { kind: "command", emotion: target };
    default:
      return null;
  }
}

export class QuickSlotBar {
  private readonly root: HTMLElement;
  private readonly cells: HTMLButtonElement[] = [];
  private readonly doc: Document;

  private bindings: (QuickBinding | null)[] =
    Array<QuickBinding | null>(QUICK_SLOT_TOTAL).fill(null);
  private pageIndex = 0;
  private bag: QuickBag = { slots: [] };
  private skills: readonly QuickSkill[] = [];
  private fingerprint = "";

  constructor(
    container: HTMLElement,
    private readonly actions: QuickSlotActions = {},
    options: QuickSlotOptions = {},
  ) {
    this.doc = container.ownerDocument ?? document;
    installStyle(this.doc);

    this.root = elem(this.doc, "div", "quickslots");
    for (let local = 0; local < QUICK_SLOTS; local++) {
      const cell = this.doc.createElement("button");
      cell.type = "button";
      cell.className = "quickslot";
      cell.dataset.index = String(local);
      cell.dataset.quickIndex = String(local);
      cell.addEventListener("click", () => this.fireLocal(local));
      cell.addEventListener("contextmenu", (event) => {
        // Clearing is the one action here that cannot be undone by repeating
        // it, so it gets the deliberate gesture -- the same bargain the bag
        // makes for dropping.
        event.preventDefault();
        this.clear(this.globalIndex(local));
      });
      this.cells.push(cell);
      this.root.append(cell);
    }

    container.append(this.root);
    this.paint();

    if (options.keys !== false) this.bindKeys();
  }

  /** The server stated the whole quick-slot table. Nothing here decides it. */
  setSlots(entries: readonly QuickSlotState[]): void {
    this.bindings = bindingsFrom(entries);
    this.repaint();
    this.actions.onChange?.();
  }

  /** The server stated the bag. An item slot names a cell in it. */
  update(bag: QuickBag): void {
    this.bag = bag;
    this.repaint();
  }

  /** The server stated the skill book. A skill slot names a vnum in it. */
  updateSkills(skills: readonly QuickSkill[]): void {
    this.skills = skills;
    this.repaint();
  }

  /** The page on show, 0-3. Client-side, and started at 0 every session the
   *  way `game.py:84` starts it. */
  get page(): number {
    return this.pageIndex;
  }

  /** Show a page, wrapping as `SetQuickPage` wraps (`PythonPlayer.cpp:692-699`). */
  setPage(n: number): void {
    const next = pageOf(n);
    if (next === this.pageIndex) return;
    this.pageIndex = next;
    this.repaint();
    this.actions.onChange?.();
  }

  /** Global index of a local slot on the page now showing. */
  globalIndex(localIndex: number): number {
    return globalIndexOf(this.pageIndex, localIndex);
  }

  /** What is on a slot, by global index, or null. */
  assigned(index: number): QuickBinding | null {
    return this.bindings[index] ?? null;
  }

  /** The global index of the first free slot on the page now showing, or null
   *  when all eight are taken. `RequestAddToEmptyLocalQuickSlot` walks the
   *  *local* slots in order and takes the first with type 0
   *  (`uiinventory.py:721-727`, `PythonPlayer.cpp:779-793`). */
  firstEmpty(): number | null {
    for (let local = 0; local < QUICK_SLOTS; local++) {
      const index = this.globalIndex(local);
      if (this.bindings[index] === null) return index;
    }
    return null;
  }

  bindItem(index: number, cell: number): boolean {
    if (!this.inTable(index) || !Number.isInteger(cell) || cell < 0) return false;
    this.actions.onAdd?.(index, SLOT_TYPE_ITEM, cell);
    return true;
  }

  /** Ask for a skill to go on a slot, by vnum. */
  bindSkill(index: number, vnum: number): boolean {
    if (!this.inTable(index) || !Number.isInteger(vnum) || vnum <= 0) return false;
    this.actions.onAdd?.(index, SLOT_TYPE_SKILL, vnum);
    return true;
  }

  /** Ask for a gesture to go on a slot, by the server's emotion id. */
  bindCommand(index: number, emotion: number): boolean {
    if (!this.inTable(index) || !Number.isInteger(emotion) || emotion < 0) return false;
    this.actions.onAdd?.(index, SLOT_TYPE_COMMAND, emotion);
    return true;
  }

  /** Ask for a slot to be emptied. `DelQuickslot` is its own verb rather than
   *  an add of type 0, because `SetQuickslot` refuses type 0
   *  (`char_quickslot.cpp:82-83`). */
  clear(index: number): void {
    if (!this.inTable(index)) return;
    this.actions.onDel?.(index);
  }

  fire(index: number): void {
    const binding = this.assigned(index);
    if (binding === null) return;
    switch (binding.kind) {
      case "item":
        this.actions.onUse?.(binding.cell);
        return;
      case "skill":
        this.actions.onUseSkill?.(binding.vnum);
        return;
      case "command":
        this.actions.onEmote?.(binding.emotion);
        return;
    }
  }

  /** Use the slot a key names. `RequestUseLocalQuickSlot` refuses a local index
   *  at or past `QUICKSLOT_MAX_COUNT_PER_LINE` before it reads anything
   *  (`PythonPlayer.cpp:812-813`). */
  fireLocal(localIndex: number): void {
    if (localIndex < 0 || localIndex >= QUICK_SLOTS) return;
    this.fire(this.globalIndex(localIndex));
  }

  get element(): HTMLElement {
    return this.root;
  }

  // -------------------------------------------------------------- internals

  private inTable(index: number): boolean {
    return Number.isInteger(index) && index >= 0 && index < QUICK_SLOT_TOTAL;
  }

  private bindKeys(): void {
    const view = this.doc.defaultView;
    if (view === null) return;
    // Capture: F1 is the browser's help key and F3 its find bar. Bubble is
    // too late -- the help tab opens over the fight.
    view.addEventListener(
      "keydown",
      (event: KeyboardEvent) => {
        if (event.metaKey || event.ctrlKey || event.altKey) return;
        // The physical key, not the character: Shift+1 is `"!"` in `key` and
        // `"Digit1"` in `code`, and the original's `DIK_1` is a scan code.
        const local = indexForCode(event.code);
        // F1-F4 are taken from the browser even while the chat box has the
        // keyboard, because there is nothing a player types F1 into. A digit
        // is a character, so it is only taken when the bar acts on it.
        if (/^F[1-4]$/.test(event.code)) event.preventDefault();
        if (local === null) return;
        if (this.actions.blocked?.() === true) return;
        event.preventDefault();
        if (event.shiftKey) {
          if (local < QUICK_SLOT_PAGES) this.setPage(local);
          return;
        }
        this.fireLocal(local);
      },
      true,
    );
  }

  private repaint(): void {
    const print = this.fingerprintOf();
    if (print === this.fingerprint) return;
    this.fingerprint = print;
    this.paint();
  }

  /** Changes exactly when the drawn bar must: which page, what is on each of
   *  its eight slots, and what the cell or skill under one looks like now. A
   *  bag frame that moved something else repaints nothing. */
  private fingerprintOf(): string {
    const parts: string[] = [String(this.pageIndex)];
    for (let local = 0; local < QUICK_SLOTS; local++) {
      const binding = this.assigned(this.globalIndex(local));
      if (binding === null) {
        parts.push("-");
      } else if (binding.kind === "skill") {
        const skill = this.skills.find((entry) => entry.vnum === binding.vnum);
        parts.push(`s:${String(binding.vnum)}:${String(skill?.level ?? 0)}`);
      } else if (binding.kind === "command") {
        parts.push(`c:${String(binding.emotion)}`);
      } else {
        const held = this.cellOf(binding.cell);
        parts.push(
          `i:${String(binding.cell)}:${String(held?.vnum ?? 0)}:${String(held?.count ?? 0)}`,
        );
      }
    }
    return parts.join("|");
  }

  /** `ui.SlotWindow.SetCoverButton`'s four default faces over one slot
   *  (`root/ui.py:1269-1272`), which a skill slot and a gesture slot each get
   *  (`uitaskbar.py:799`, `uitaskbar.py:814`) and an item slot does not. */
  private cover(disabled: boolean): HTMLElement {
    const plate = elem(this.doc, "span", "m2-slot-cover");
    if (disabled) plate.setAttribute("aria-disabled", "true");
    return plate;
  }

  private cellOf(cell: number): QuickBagSlot | undefined {
    return this.bag.slots.find((slot) => slot.slot === cell);
  }

  private paint(): void {
    for (let local = 0; local < QUICK_SLOTS; local++) {
      const cell = this.cells[local];
      const binding = this.assigned(this.globalIndex(local));
      const key = elem(this.doc, "span", "quickslot-key", keyFor(local));

      if (binding === null) {
        cell.className = "quickslot quickslot-free";
        cell.removeAttribute("title");
        const plus = elem(this.doc, "span", "quickslot-plus", "+");
        cell.replaceChildren(key, plus);
        continue;
      }

      if (binding.kind === "skill") {
        const skill = this.skills.find((entry) => entry.vnum === binding.vnum);
        const name = skill?.name ?? `skill ${String(binding.vnum)}`;
        const ready = skill !== undefined && hotkeyableSkill(skill);
        cell.className = ready ? "quickslot" : "quickslot quickslot-empty";
        cell.title = ready
          ? `${keyFor(local)} — ${name} · right-click to clear`
          : `${keyFor(local)} — ${name}, not learned · right-click to clear`;
        const art =
          skill !== undefined
            ? (skillIconNode(skill.icon, skillGradeFor(skill.level), this.doc) ??
              this.doc.createTextNode(name))
            : this.doc.createTextNode(name);
        cell.replaceChildren(key, art, this.cover(!ready));
        continue;
      }

      if (binding.kind === "command") {
        cell.className = "quickslot";
        cell.title = `${keyFor(local)} — gesture ${String(binding.emotion)} · right-click to clear`;
        cell.replaceChildren(key, this.doc.createTextNode("☺"), this.cover(false));
        continue;
      }

      const held = this.cellOf(binding.cell);
      const count = held?.count ?? 0;
      const name = held?.name ?? `cell ${String(binding.cell)}`;
      cell.className = count > 0 ? "quickslot" : "quickslot quickslot-empty";
      cell.title =
        count > 0
          ? `${keyFor(local)} — ${name} (${String(count)}) · right-click to clear`
          : `${keyFor(local)} — ${name}, empty · right-click to clear`;

      const art = itemArtNode(held?.vnum ?? 0, held?.kind ?? USABLE_KIND, this.doc);
      const badge = elem(this.doc, "span", "quickslot-count", String(count));
      cell.replaceChildren(key, art, badge);
    }
  }
}
